import { useState, FormEvent } from 'react';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

interface FormData {
  destino: string;
  pessoas: string;
  tipoViagem: string;
  incluirVisto: boolean;
  nome: string;
  telefone: string;
}

export default function BudgetCalculator() {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState<FormData>({
    destino: '',
    pessoas: '1',
    tipoViagem: '',
    incluirVisto: false,
    nome: '',
    telefone: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showResult, setShowResult] = useState(false);

  const destinos = [
    { value: 'portugal', label: '🇵🇹 Portugal', popular: true },
    { value: 'brasil', label: '🇧🇷 Brasil', popular: true },
    { value: 'dubai', label: '🇦🇪 Dubai', popular: true },
    { value: 'china', label: '🇨🇳 China' },
    { value: 'espanha', label: '🇪🇸 Espanha' },
    { value: 'franca', label: '🇫🇷 França' },
    { value: 'alemanha', label: '🇩🇪 Alemanha' },
    { value: 'suica', label: '🇨🇭 Suíça' },
    { value: 'belgica', label: '🇧🇪 Bélgica' },
    { value: 'canada', label: '🇨🇦 Canadá' },
    { value: 'eua', label: '🇺🇸 Estados Unidos' },
    { value: 'uk', label: '🇬🇧 Reino Unido' }
  ];

  const tiposViagem = [
    { 
      value: 'economico', 
      label: 'Económico', 
      desc: 'Viagem simples e funcional',
      icon: 'ri-money-dollar-circle-line'
    },
    { 
      value: 'conforto', 
      label: 'Conforto', 
      desc: 'Equilíbrio entre preço e qualidade',
      icon: 'ri-star-line'
    },
    { 
      value: 'premium', 
      label: 'Premium', 
      desc: 'Experiência superior',
      icon: 'ri-vip-crown-line'
    },
    { 
      value: 'luxo', 
      label: 'Luxo', 
      desc: 'O melhor que oferecemos',
      icon: 'ri-gem-line'
    }
  ];

  const handleNext = () => {
    if (step === 1 && !formData.destino) return;
    if (step === 2 && !formData.tipoViagem) return;
    if (step < 3) {
      setStep(step + 1);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!formData.nome || !formData.telefone) return;

    setIsSubmitting(true);

    try {
      // Salvar na base de dados
      const destinoLabel = destinos.find(d => d.value === formData.destino)?.label || formData.destino;
      const tipoLabel = tiposViagem.find(t => t.value === formData.tipoViagem)?.label || formData.tipoViagem;

      const mensagemCompleta = `Destino: ${destinoLabel} | Pessoas: ${formData.pessoas} | Tipo: ${tipoLabel} | Visto: ${formData.incluirVisto ? 'Sim' : 'Não'}`;

      await supabase
        .from('contactos_fftravel')
        .insert([
          {
            nome: formData.nome,
            email: formData.telefone, // Usando telefone no campo email temporariamente
            telefone: formData.telefone,
            assunto: 'Pedido de Cotação de Viagem',
            mensagem: mensagemCompleta,
            status: 'novo'
          }
        ]);

      // Preparar mensagem para WhatsApp
      const mensagem = `🌍 INTERESSE EM VIAGEM - FF TRAVEL

👤 *DADOS DO CLIENTE*
Nome: ${formData.nome}
Telefone: ${formData.telefone}

✈️ *PREFERÊNCIAS DE VIAGEM*
Destino: ${destinoLabel}
Número de pessoas: ${formData.pessoas}
Tipo de viagem: ${tipoLabel}
Incluir apoio ao visto: ${formData.incluirVisto ? 'Sim' : 'Não'}

💬 *MENSAGEM*
Cliente interessado em receber cotação personalizada para esta viagem.`;

      const whatsappUrl = `https://wa.me/244936769113?text=${encodeURIComponent(mensagem)}`;
      window.open(whatsappUrl, '_blank');

      setShowResult(true);
    } catch (error) {
      console.error('Erro:', error);
      alert('Erro ao enviar pedido. Por favor, tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (showResult) {
    return (
      <div className="bg-gradient-to-br from-[#1e40af] to-[#1e3a8a] rounded-3xl p-12 text-white text-center">
        <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
          <i className="ri-whatsapp-line text-4xl"></i>
        </div>
        <h3 className="text-3xl font-bold mb-4">Muito Obrigado! 🎉</h3>
        <p className="text-xl text-white/90 mb-6">
          As suas preferências foram enviadas!
        </p>
        <p className="text-white/80 mb-8">
          A nossa equipa irá contactá-lo em breve com uma cotação personalizada exclusiva para si.
        </p>
        <button
          onClick={() => {
            setStep(1);
            setShowResult(false);
            setFormData({
              destino: '',
              pessoas: '1',
              tipoViagem: '',
              incluirVisto: false,
              nome: '',
              telefone: ''
            });
          }}
          className="bg-white/20 text-white px-8 py-3 rounded-full hover:bg-white/30 transition-all font-semibold"
        >
          Nova Consulta
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-br from-[#1e40af] to-[#1e3a8a] p-8 text-white text-center">
        <div className="inline-block bg-[#ff6b35] px-4 py-2 rounded-full text-sm font-bold mb-4 animate-bounce">
          ✨ Descubra Sua Viagem Ideal
        </div>
        <h2 className="text-3xl md:text-4xl font-bold mb-3">
          Planeador de Viagem Personalizado
        </h2>
        <p className="text-white/90 text-lg">
          Conte-nos suas preferências e receba uma proposta exclusiva
        </p>
        
        {/* Progress Bar */}
        <div className="mt-8 bg-white/20 rounded-full h-2 overflow-hidden">
          <div 
            className="bg-[#ff6b35] h-full transition-all duration-500 ease-out"
            style={{ width: `${(step / 3) * 100}%` }}
          ></div>
        </div>
        <p className="text-white/70 text-sm mt-2">Passo {step} de 3</p>
      </div>

      <div className="p-8">
        {/* Step 1: Destino */}
        {step === 1 && (
          <div className="animate-fade-in">
            <h3 className="text-2xl font-bold text-[#1e3a8a] mb-6 text-center">
              🌍 Para onde sonha viajar?
            </h3>
            <div className="space-y-3 mb-8">
              {/* Destinos Populares */}
              <p className="text-sm text-[#ff6b35] font-semibold mb-3">⭐ MAIS PROCURADOS:</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
                {destinos.filter(d => d.popular).map((destino) => (
                  <button
                    key={destino.value}
                    onClick={() => setFormData({...formData, destino: destino.value})}
                    className={`p-4 rounded-xl border-2 text-left transition-all hover:scale-105 ${
                      formData.destino === destino.value
                        ? 'border-[#ff6b35] bg-[#ff6b35]/10 ring-2 ring-[#ff6b35]/20'
                        : 'border-gray-200 hover:border-[#1e40af] hover:bg-gray-50'
                    }`}
                  >
                    <span className="text-lg font-semibold">{destino.label}</span>
                    <div className="text-xs text-gray-500 mt-1">✨ Destino popular</div>
                  </button>
                ))}
              </div>

              {/* Outros Destinos */}
              <p className="text-sm text-gray-600 font-semibold mb-3">🌟 OUTROS DESTINOS:</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {destinos.filter(d => !d.popular).map((destino) => (
                  <button
                    key={destino.value}
                    onClick={() => setFormData({...formData, destino: destino.value})}
                    className={`p-3 rounded-lg border-2 text-left transition-all hover:scale-105 ${
                      formData.destino === destino.value
                        ? 'border-[#ff6b35] bg-[#ff6b35]/10'
                        : 'border-gray-200 hover:border-[#1e40af] hover:bg-gray-50'
                    }`}
                  >
                    <span className="text-sm font-semibold">{destino.label}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="flex justify-center">
              <button
                onClick={handleNext}
                disabled={!formData.destino}
                className={`px-8 py-4 rounded-full font-bold text-lg transition-all ${
                  formData.destino
                    ? 'bg-[#ff6b35] text-white hover:bg-[#ff5722] hover:scale-105 shadow-lg'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                Continuar →
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Tipo de Viagem */}
        {step === 2 && (
          <div className="animate-fade-in">
            <h3 className="text-2xl font-bold text-[#1e3a8a] mb-2 text-center">
              ✨ Que experiência deseja?
            </h3>
            <p className="text-gray-600 text-center mb-8">Cada tipo oferece uma experiência única</p>
            
            <div className="space-y-4 mb-8">
              <div className="flex items-center gap-4 mb-4">
                <label className="text-gray-700 font-semibold">Quantas pessoas:</label>
                <select
                  value={formData.pessoas}
                  onChange={(e) => setFormData({...formData, pessoas: e.target.value})}
                  className="border-2 border-gray-200 rounded-lg px-3 py-2 focus:border-[#1e40af] focus:outline-none"
                >
                  <option value="1">1 pessoa</option>
                  <option value="2">2 pessoas</option>
                  <option value="3">3 pessoas</option>
                  <option value="4">4 pessoas</option>
                  <option value="5+">5 ou mais</option>
                </select>
              </div>

              {tiposViagem.map((tipo) => (
                <button
                  key={tipo.value}
                  onClick={() => setFormData({...formData, tipoViagem: tipo.value})}
                  className={`w-full p-6 rounded-xl border-2 text-left transition-all hover:scale-102 ${
                    formData.tipoViagem === tipo.value
                      ? 'border-[#ff6b35] bg-[#ff6b35]/10 ring-2 ring-[#ff6b35]/20'
                      : 'border-gray-200 hover:border-[#1e40af] hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-[#1e40af]/10 rounded-full flex items-center justify-center">
                      <i className={`${tipo.icon} text-2xl text-[#1e40af]`}></i>
                    </div>
                    <div>
                      <h4 className="text-xl font-bold text-[#1e3a8a]">{tipo.label}</h4>
                      <p className="text-gray-600">{tipo.desc}</p>
                      <p className="text-sm text-[#ff6b35] font-semibold mt-1">
                        💫 Cotação personalizada disponível
                      </p>
                    </div>
                  </div>
                </button>
              ))}

              <label className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-all">
                <input
                  type="checkbox"
                  checked={formData.incluirVisto}
                  onChange={(e) => setFormData({...formData, incluirVisto: e.target.checked})}
                  className="w-5 h-5 text-[#1e40af] border-2 border-gray-300 rounded focus:ring-2 focus:ring-[#1e40af]"
                />
                <div>
                  <span className="font-semibold text-gray-800">Preciso de apoio para obter visto</span>
                  <p className="text-sm text-gray-600">Ajudamos com todo o processo</p>
                </div>
              </label>
            </div>

            <div className="flex justify-center gap-4">
              <button
                onClick={() => setStep(1)}
                className="px-6 py-3 rounded-full border-2 border-gray-300 text-gray-600 hover:bg-gray-50 transition-all"
              >
                ← Voltar
              </button>
              <button
                onClick={handleNext}
                disabled={!formData.tipoViagem}
                className={`px-8 py-4 rounded-full font-bold text-lg transition-all ${
                  formData.tipoViagem
                    ? 'bg-[#ff6b35] text-white hover:bg-[#ff5722] hover:scale-105 shadow-lg'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                Quase Lá! →
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Contacto */}
        {step === 3 && (
          <div className="animate-fade-in">
            <h3 className="text-2xl font-bold text-[#1e3a8a] mb-2 text-center">
              📞 Para onde enviamos sua cotação exclusiva?
            </h3>
            <p className="text-gray-600 text-center mb-8">
              Última etapa! Nossa equipa irá preparar uma proposta personalizada
            </p>

            {/* Resumo */}
            <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-6 mb-8">
              <h4 className="font-bold text-[#1e3a8a] mb-4 flex items-center gap-2">
                <i className="ri-clipboard-line"></i>
                Resumo das suas preferências:
              </h4>
              <div className="space-y-2 text-sm">
                <p><span className="font-semibold">Destino:</span> {destinos.find(d => d.value === formData.destino)?.label}</p>
                <p><span className="font-semibold">Pessoas:</span> {formData.pessoas}</p>
                <p><span className="font-semibold">Estilo:</span> {tiposViagem.find(t => t.value === formData.tipoViagem)?.label}</p>
                <p><span className="font-semibold">Visto:</span> {formData.incluirVisto ? 'Com apoio' : 'Não precisa de apoio'}</p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Nome completo *
                </label>
                <input
                  type="text"
                  value={formData.nome}
                  onChange={(e) => setFormData({...formData, nome: e.target.value})}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#1e40af] focus:outline-none transition-all"
                  placeholder="Como podemos chamá-lo?"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Telefone / WhatsApp *
                </label>
                <input
                  type="tel"
                  value={formData.telefone}
                  onChange={(e) => setFormData({...formData, telefone: e.target.value})}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#1e40af] focus:outline-none transition-all"
                  placeholder="+244 900 000 000"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  💬 Entraremos em contacto via WhatsApp com sua cotação exclusiva
                </p>
              </div>

              <div className="flex justify-center gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => setStep(2)}
                  className="px-6 py-3 rounded-full border-2 border-gray-300 text-gray-600 hover:bg-gray-50 transition-all"
                >
                  ← Voltar
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting || !formData.nome || !formData.telefone}
                  className={`px-8 py-4 rounded-full font-bold text-lg transition-all ${
                    formData.nome && formData.telefone
                      ? 'bg-[#ff6b35] text-white hover:bg-[#ff5722] hover:scale-105 shadow-lg'
                      : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  {isSubmitting ? (
                    <span className="flex items-center gap-2">
                      <i className="ri-loader-4-line animate-spin"></i>
                      Enviando...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <i className="ri-whatsapp-line"></i>
                      Receber Cotação Exclusiva
                    </span>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
